/**
 * Divisão da "Rolagem e outros ajustes" entre Futuro Plataforma e Futuro Hub na tela
 * "Avaliar solicitação" (card 1219573563390460).
 *
 * O backend grava, na criação da solicitação, quanto da rolagem é de cada empresa
 * (`rolling_split`). Aqui só se monta a tela: os dois blocos, as duas transferências e as
 * conferências. Nenhuma regra de dinheiro é decidida no front.
 *
 * ⚠️ O Total da fatura continua sendo a conta de hoje (assinatura + entrevista + solução
 * financeira + adjustments_amount): o valor exibido não pode mudar. Se Plataforma + Hub não
 * fechar com ele, a divisão é descartada e a tela volta ao layout de hoje — uma divisão que não
 * fecha nunca aparece.
 */

const PLATFORM = 'platform'
const HUB = 'hub'
const UNASSIGNED = 'unassigned'

// Itens por grupo na composição: rolagens com milhares de lançamentos travariam a sidebar.
const MAX_VISIBLE_ITEMS = 50

const toNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

// Centavos arredondados para longe do zero, como o Intl do filtro toCurrency.
const toCents = value => {
  const number = toNumber(value)
  return Math.sign(number) * Math.round(Math.abs(number) * 100)
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
})

// Mesmo texto do filtro toCurrency; o `+ 0` troca -0 por 0, para nunca sair "-R$ 0,00".
const formatCurrency = value => currencyFormatter.format(toCents(value) / 100 + 0)

// Valor para colar no banco: sem "R$" e sem ponto de milhar ("6662,20").
const formatCopy = value => {
  const cents = Math.abs(toCents(value))
  return `${Math.floor(cents / 100)},${String(cents % 100).padStart(2, '0')}`
}

const quantity = count => (count === 1 ? '1 lançamento' : `${count} lançamentos`)

const sumItems = items => items.reduce((total, item) => total + toNumber(item.amount), 0)

const presentItems = items => ({
  count: items.length,
  hidden: Math.max(0, items.length - MAX_VISIBLE_ITEMS),
  list: items.slice(0, MAX_VISIBLE_ITEMS).map(item => ({
    title: item.title,
    detail: item.detail,
    amount: toNumber(item.amount),
    negative: toCents(item.amount) < 0,
  })),
})

const transfersFor = (platformTotal, hubTotal) => {
  const platform = toCents(platformTotal)
  const hub = toCents(hubTotal)

  // Não existe transferência negativa: o bloco negativo fica em R$ 0,00 e o valor sai da outra.
  if (platform < 0 && hub >= 0) {
    return {
      platform: 0,
      hub: Math.max(0, platformTotal + hubTotal),
      compensation: { from: PLATFORM, amount: -platformTotal },
    }
  }
  if (hub < 0 && platform >= 0) {
    return {
      platform: Math.max(0, platformTotal + hubTotal),
      hub: 0,
      compensation: { from: HUB, amount: -hubTotal },
    }
  }
  if (platform < 0 && hub < 0) {
    return { platform: 0, hub: 0, compensation: null }
  }
  return { platform: platformTotal, hub: hubTotal, compensation: null }
}

const alertFor = ({
  divergent, compensation, invoiceTotal, approvedAmount, platformTotal, hubTotal, transfers, amountLabel, readonly,
}) => {
  if (divergent) {
    return {
      variant: 'danger',
      title: `Total da fatura diferente do ${amountLabel}`,
      text: `Total da fatura ${formatCurrency(invoiceTotal)} · ${amountLabel} ${formatCurrency(approvedAmount)} · `
        + `diferença ${formatCurrency(approvedAmount - invoiceTotal)}. As transferências por empresa não foram calculadas, `
        + (readonly
          ? 'porque não fecham com o valor da solicitação. Confira os saques já feitos nesta competência.'
          : 'porque não fecham com o valor que será pago. Confira os saques já feitos nesta competência antes de aprovar.'),
    }
  }
  if (compensation && compensation.from === HUB) {
    return {
      variant: 'warning',
      title: 'Total Futuro Hub negativo: compensado na Futuro Plataforma',
      text: `O bloco da Futuro Hub fechou em ${formatCurrency(hubTotal)}. Não existe transferência negativa: `
        + `a da Futuro Hub fica ${formatCurrency(0)} e a da Futuro Plataforma cai de ${formatCurrency(platformTotal)} `
        + `para ${formatCurrency(transfers.platform)}. O total pago ao PN não muda: ${formatCurrency(invoiceTotal)}.`,
    }
  }
  if (compensation && compensation.from === PLATFORM) {
    return {
      variant: 'warning',
      title: 'Total plataforma Futuro negativo: compensado na Futuro Hub',
      text: `O bloco da Futuro Plataforma fechou em ${formatCurrency(platformTotal)}. Não existe transferência negativa: `
        + `a da Futuro Plataforma fica ${formatCurrency(0)} e a da Futuro Hub cai de ${formatCurrency(hubTotal)} `
        + `para ${formatCurrency(transfers.hub)}. O total pago ao PN não muda: ${formatCurrency(invoiceTotal)}.`,
    }
  }
  return null
}

/**
 * Monta a tela nova a partir do retrato da solicitação (`bill`) e da divisão gravada
 * (`split`). Devolve null quando não há divisão ou quando ela não fecha: nos dois casos a tela
 * fica no layout de hoje.
 *
 * `readonly` é o "Detalhes da solicitação": abre em qualquer status, inclusive aprovada e paga,
 * então o valor não pode se chamar "a ser aprovado".
 */
const buildRollingSplitView = (bill, split, { readonly = false } = {}) => {
  if (!bill || !split) return null

  const subscriptions = toNumber(bill.subscriptions_amount)
  const interviews = toNumber(bill.interviews_amount)
  const solutions = toNumber(bill.financial_solutions_amount)
  const platformAdjustments = toNumber(bill.platform_adjustments_amount)
  const hubAdjustments = toNumber(bill.hub_adjustments_amount)
  const platformRolling = toNumber(split.platform_rolling_amount)
  const hubRolling = toNumber(split.hub_rolling_amount)
  const unassignedRolling = toNumber(split.unassigned_rolling_amount)

  const platformTotal = subscriptions + interviews + platformAdjustments + platformRolling
  const hubTotal = solutions + hubAdjustments + hubRolling
  const invoiceTotal = subscriptions + interviews + solutions + toNumber(bill.adjustments_amount)

  const items = Array.isArray(split.items) ? split.items : []
  const platformItems = items.filter(item => item.group === PLATFORM)
  const hubItems = items.filter(item => item.group === HUB)
  const unassignedItems = items.filter(item => item.group === UNASSIGNED)

  const closes = (a, b) => Math.abs(toCents(a) - toCents(b)) <= 1
  if (
    !closes(platformTotal + hubTotal, invoiceTotal)
    || !closes(sumItems(platformItems), platformRolling)
    || !closes(sumItems(hubItems) + sumItems(unassignedItems), hubRolling)
  ) {
    return null
  }

  const approvedAmount = toNumber(bill.amount)
  const amountLabel = readonly ? 'Valor da solicitação' : 'Valor a ser aprovado'
  const divergent = toCents(approvedAmount) !== toCents(invoiceTotal)
  const transfers = transfersFor(platformTotal, hubTotal)
  const compensation = divergent ? null : transfers.compensation

  const platformLines = []
  const hubLines = []
  let platformBadge = null
  let hubBadge = null
  if (compensation && compensation.from === HUB) {
    platformLines.push(`Total plataforma Futuro ${formatCurrency(platformTotal)} − ${formatCurrency(compensation.amount)} compensados da Futuro Hub`)
    hubBadge = 'Não transferir'
    hubLines.push(`Bloco em ${formatCurrency(hubTotal)}, descontado da Futuro Plataforma`)
  }
  if (compensation && compensation.from === PLATFORM) {
    platformBadge = 'Não transferir'
    platformLines.push(`Bloco em ${formatCurrency(platformTotal)}, descontado da Futuro Hub`)
    hubLines.push(`Total Futuro Hub ${formatCurrency(hubTotal)} − ${formatCurrency(compensation.amount)} compensados da Futuro Plataforma`)
  }
  const hasUnassigned = toCents(unassignedRolling) !== 0
  if (hasUnassigned) {
    hubLines.push(`Inclui ${formatCurrency(unassignedRolling)} sem empresa pagadora`)
  }

  const hubAssignedRolling = hubRolling - unassignedRolling

  return {
    divergent,
    amountLabel,
    alert: alertFor({
      divergent, compensation, invoiceTotal, approvedAmount, platformTotal, hubTotal, transfers, amountLabel, readonly,
    }),
    transfers: {
      platform: divergent ? null : transfers.platform,
      hub: divergent ? null : transfers.hub,
      sum: divergent ? null : transfers.platform + transfers.hub,
      platformBadge,
      hubBadge,
      platformLines,
      hubLines,
    },
    platform: {
      interviews,
      subscriptions,
      adjustments: platformAdjustments,
      rolling: platformRolling,
      total: platformTotal,
      negative: toCents(platformTotal) < 0,
      compensated: Boolean(compensation && compensation.from === PLATFORM),
      composition: {
        ...presentItems(platformItems),
        label: quantity(platformItems.length),
        footer: `Soma igual à linha Rolagem e outros ajustes da Futuro Plataforma (${formatCurrency(platformRolling)}).`,
      },
    },
    hub: {
      solutions,
      adjustments: hubAdjustments,
      rolling: hubRolling,
      rollingNegative: toCents(hubRolling) < 0,
      unassigned: unassignedRolling,
      hasUnassigned,
      total: hubTotal,
      negative: toCents(hubTotal) < 0,
      compensated: Boolean(compensation && compensation.from === HUB),
      composition: {
        assigned: { ...presentItems(hubItems), label: quantity(hubItems.length), total: hubAssignedRolling },
        unassigned: { ...presentItems(unassignedItems), label: quantity(unassignedItems.length), total: unassignedRolling },
        label: quantity(hubItems.length + unassignedItems.length),
        footer: unassignedItems.length
          ? `Futuro Hub ${formatCurrency(hubAssignedRolling)} + Sem empresa pagadora ${formatCurrency(unassignedRolling)} = `
            + `${formatCurrency(hubRolling)}, igual à linha Rolagem e outros ajustes da Futuro Hub.`
          : `Soma igual à linha Rolagem e outros ajustes da Futuro Hub (${formatCurrency(hubRolling)}).`,
      },
    },
    invoiceTotal,
    approvedAmount,
    rollingCaption: `Inclui ${formatCurrency(platformRolling + hubRolling)} de rolagem: `
      + `${formatCurrency(platformRolling)} na Plataforma e ${formatCurrency(hubRolling)} na Hub.`,
    conference: divergent
      ? { equal: false, text: `Diferença de ${formatCurrency(approvedAmount - invoiceTotal)}` }
      : { equal: true, text: 'Valores iguais' },
    hubInvoiceAmount: split.hub_invoice_amount === null || split.hub_invoice_amount === undefined
      ? null
      : toNumber(split.hub_invoice_amount),
  }
}

module.exports = {
  buildRollingSplitView,
  formatCopy,
  formatCurrency,
  MAX_VISIBLE_ITEMS,
}
